/**
 * Per-feature metadata shown on every feature card (Config, Manual
 * Simulator, Automated Simulator):
 *
 *  - `effect`: how the feature changes the trade set —
 *      "adds"    = only creates entries the core rules would not take
 *                  (overrides / early entries);
 *      "removes" = only filters entries out (gates);
 *      "both"    = can change wins AND losses (exits, fill timing, core band);
 *      "neutral" = sizing / bookkeeping only — never changes which windows
 *                  are traded or whether they win.
 *  - `cost`: simulation speed, 1 (green, cached numpy/numba) .. 5 (red,
 *      loads and simulates the counterpart market). Live trading is
 *      unaffected — every gate runs once per second regardless.
 *
 * Keys are the feature's enable toggle (`*_enabled`) or, for cards without
 * a toggle, the group's `metaKey`.
 */

import { Badge, Group, Text, Tooltip } from "@mantine/core";
import { IconBroadcast, IconFlask } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { SimulationGateTimings } from "../api/schemas";

export type FeatureEffect = "adds" | "removes" | "both" | "neutral";
export type FeatureCost = 1 | 2 | 3 | 4 | 5;
/** Where a setting takes effect. Default "both" (live bot and simulators). */
export type FeatureScope = "both" | "sim" | "live";

export interface FeatureMeta {
  effect: FeatureEffect;
  cost: FeatureCost;
  scope?: FeatureScope;
  /** Optional caveat appended to the tooltips. */
  note?: string;
}

export const EFFECT_LABEL: Record<FeatureEffect, { label: string; color: string; help: string }> = {
  adds: { label: "Adds trades", color: "teal", help: "Only creates entries the core rules would not have taken. Can add wins AND losses." },
  removes: { label: "Filters trades", color: "orange", help: "Only removes entries. Can only drop wins or losses that the remaining rules would have taken." },
  both: { label: "Changes outcomes", color: "blue", help: "Can change both wins and losses (e.g. exits, fill timing, the odds band itself)." },
  neutral: { label: "Sizing / P&L only", color: "gray", help: "Does not change which windows are traded or whether they win; only size, streak bookkeeping or P&L." },
};

export const COST_LABEL: Record<FeatureCost, { short: string; label: string; color: string }> = {
  1: { short: "Very fast", label: "Very fast — cached numpy/numba path, sweeps at full speed", color: "#2f9e44" },
  2: { short: "Fast", label: "Fast — small per-candidate Python check", color: "#94c11f" },
  3: { short: "Moderate", label: "Moderate — forces the per-tick Python reference scan for every combo", color: "#f59f00" },
  4: { short: "Slow", label: "Slow — reference scan plus extra per-window state", color: "#f76707" },
  5: { short: "Slowest", label: "Slowest — also loads and simulates the counterpart market", color: "#e03131" },
};

export const FEATURE_META: Record<string, FeatureMeta> = {
  // Core / structural
  core: { effect: "both", cost: 1, note: "Defines the trade set every other feature filters or extends." },
  candle_filter_enabled: { effect: "removes", cost: 1 },
  loss_streak_threshold: { effect: "neutral", cost: 1 },
  allow_multiple_orders_per_window: { effect: "adds", cost: 1, scope: "live", note: "Live only — the simulators always cap at one order per window." },
  entry_confirm: { effect: "removes", cost: 3 },
  sim_execution_delay: { effect: "both", cost: 3, scope: "sim", note: "Sim-only: turns some entries into no-fills and moves the fill price." },
  late_buy_enabled: { effect: "adds", cost: 1 },
  force_buy_enabled: { effect: "adds", cost: 1, scope: "live", note: "Manual dashboard action; no simulated effect." },
  // Entry gates
  open_price_gate_enabled: { effect: "removes", cost: 1 },
  open_price_override_enabled: { effect: "adds", cost: 1 },
  open_price_cushion_enabled: { effect: "removes", cost: 1 },
  prev_windows_check_enabled: { effect: "removes", cost: 1 },
  trade_time_ranges_enabled: { effect: "removes", cost: 1 },
  trade_days_enabled: { effect: "removes", cost: 1 },
  odds_momentum_enabled: { effect: "removes", cost: 2 },
  pre_order_metrics_enabled: { effect: "removes", cost: 2 },
  pre_decision_avg_odds_enabled: { effect: "removes", cost: 2 },
  band_dwell_enabled: { effect: "removes", cost: 3 },
  flip_gate_enabled: { effect: "removes", cost: 3 },
  peak_drawdown_gate_enabled: { effect: "removes", cost: 3 },
  vol_cushion_gate_enabled: { effect: "removes", cost: 3 },
  underlying_velocity_gate_enabled: { effect: "removes", cost: 3 },
  open_side_gate_enabled: { effect: "removes", cost: 3 },
  max_spread_gate_enabled: { effect: "removes", cost: 3 },
  kronos_check_enabled: { effect: "removes", cost: 3, note: "Reads the precomputed forecast cache; uncached windows fail closed." },
  counterpart_confirm_gate_enabled: { effect: "removes", cost: 4, note: "Loads the counterpart window's ticks." },
  // Overrides
  counterpart_override_enabled: { effect: "adds", cost: 5 },
  counterpart_match_order_enabled: { effect: "adds", cost: 5, note: "Pre-simulates the counterpart's own config first." },
  favorite_hold_enabled: { effect: "adds", cost: 4 },
  // Exits
  take_profit_enabled: { effect: "both", cost: 1 },
  stop_loss_enabled: { effect: "both", cost: 1, note: "The BTC-delta sub-stop uses the slower Python exit scan." },
  momentum_sell_enabled: { effect: "both", cost: 3 },
  exit_require_underlying_breach: { effect: "both", cost: 2 },
  // Sizing / bookkeeping
  skip_after_wins_enabled: { effect: "removes", cost: 1 },
  cascading_sizes_enabled: { effect: "neutral", cost: 1 },
  compounding_enabled: { effect: "neutral", cost: 1 },
  hedge_enabled: { effect: "neutral", cost: 1, scope: "sim", note: "Settlement overlay; the primary entry set is unchanged. Not implemented in the live bot — the hedge leg is simulated only." },
};

export function resolveFeatureMeta(key: string | null | undefined): FeatureMeta | null {
  if (!key) return null;
  return FEATURE_META[key] ?? null;
}

/** The grade a card displays: measured when enough data exists, else the hand-classified default. */
export function effectiveGrade(
  key: string | null | undefined,
  timings: SimulationGateTimings | undefined,
): { grade: FeatureCost; measured: boolean } | null {
  const meta = resolveFeatureMeta(key);
  if (!meta) return null;
  const m = key ? timings?.features[key] : undefined;
  if (m?.measured && m.grade) return { grade: m.grade as FeatureCost, measured: true };
  return { grade: meta.cost, measured: false };
}

/**
 * Measured per-feature simulation cost, aggregated by the data-api from
 * every run's `gate_timings` (see `sim/gate-timing-aggregate.ts`). Once a
 * feature has enough combos behind it the measured grade replaces the
 * hand-classified default on the card. Cached for 5 minutes; a failing
 * endpoint simply leaves the defaults in place.
 */
export function useGateTimings() {
  return useQuery({
    queryKey: ["simulations", "gate-timings"],
    queryFn: () => api.simulations.gateTimings(),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

function fmtSecPerKtick(v: number | null | undefined): string {
  if (v == null) return "n/a";
  if (v >= 1) return `${v.toFixed(2)} s`;
  return `${(v * 1000).toFixed(1)} ms`;
}

export const SCOPE_LABEL: Record<FeatureScope, { label: string; color: string; help: string }> = {
  both: { label: "Sim + Live", color: "green", help: "Runs the same way in the simulators and in the live trading bot." },
  sim: { label: "Sim only", color: "violet", help: "Only affects simulations. The live bot ignores this setting." },
  live: { label: "Live only", color: "red", help: "Only affects the live trading bot. Simulations ignore this setting." },
};

function ScopeIcons({ scope }: { scope: FeatureScope }) {
  return (
    <Group gap={2} wrap="nowrap" component="span">
      {scope !== "live" ? <IconFlask size={12} /> : null}
      {scope !== "sim" ? <IconBroadcast size={12} /> : null}
    </Group>
  );
}

export function FeatureMetaBadges({ metaKey, size = "sm" }: { metaKey: string | null | undefined; size?: "xs" | "sm" }) {
  const meta = resolveFeatureMeta(metaKey);
  const timings = useGateTimings();
  if (!meta) return null;
  const effect = EFFECT_LABEL[meta.effect];
  const scope = SCOPE_LABEL[meta.scope ?? "both"];
  const note = meta.note ? ` ${meta.note}` : "";
  const measured = metaKey ? timings.data?.features[metaKey] : undefined;
  const grade: FeatureCost = effectiveGrade(metaKey, timings.data)?.grade ?? meta.cost;
  const cost = COST_LABEL[grade];
  const minCombos = timings.data?.min_combos_for_measured ?? 50;
  const speedTip = measured?.measured
    ? `Measured over ${measured.runs} run(s) / ${measured.combos_on.toLocaleString()} combos with it on`
      + (measured.basis === "marginal"
        ? `: ${fmtSecPerKtick(measured.sec_per_ktick_on)} per 1k ticks vs ${fmtSecPerKtick(measured.sec_per_ktick_off)} with it off `
          + `(${measured.marginal_ratio != null ? `+${Math.round(measured.marginal_ratio * 100)}%` : ""}).`
        : `: this gate took ${measured.gate_share != null ? `${(measured.gate_share * 100).toFixed(1)}%` : "n/a"} of sampled combo time.`)
      + ` ${cost.label}.`
    : `Estimated (hand-classified): ${cost.label}.`
      + (measured ? ` Measured data so far: ${measured.combos_on.toLocaleString()} combos on / ${measured.combos_off.toLocaleString()} off — needs ${minCombos} of each.` : " No measured runs yet.")
      + note;
  return (
    <Group gap={8} mt={4} wrap="wrap" className="feature-meta">
      <Tooltip label={scope.help} multiline w={280} withArrow>
        <Badge
          size={size}
          variant="light"
          color={scope.color}
          leftSection={<ScopeIcons scope={meta.scope ?? "both"} />}
          style={{ cursor: "help" }}
          aria-label={scope.label}
        >
          {scope.label}
        </Badge>
      </Tooltip>
      <Tooltip label={`${effect.help}${note}`} multiline w={320} withArrow>
        <Badge size={size} variant="light" color={effect.color} style={{ cursor: "help" }}>
          {effect.label}
        </Badge>
      </Tooltip>
      <Tooltip label={speedTip} multiline w={340} withArrow>
        <Group gap={6} wrap="nowrap" style={{ cursor: "help", whiteSpace: "nowrap" }} aria-label={`Simulation speed ${grade} of 5${measured?.measured ? " (measured)" : " (estimated)"}`}>
          <Text size="xs" c="dimmed">Sim speed</Text>
          <Group gap={2} wrap="nowrap">
            {[1, 2, 3, 4, 5].map((i) => (
              <span
                key={i}
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 8,
                  borderRadius: 2,
                  background: i <= grade ? COST_LABEL[i as FeatureCost].color : "var(--mantine-color-default-border)",
                  opacity: i <= grade ? 1 : 0.6,
                }}
              />
            ))}
          </Group>
          <Text size="xs" fw={600} style={{ color: cost.color }}>{cost.short}</Text>
          <Text size="xs" c="dimmed">{measured?.measured ? "measured" : "est."}</Text>
        </Group>
      </Tooltip>
    </Group>
  );
}
